import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Building2, ExternalLink } from "lucide-react";

const EMPTY = { name: "", description: "", sector: "", website: "", country: "", city: "", size: "" };
const SIZES = ["1-10", "11-50", "51-200", "201-500", "500+"];

export default function EmployerCompany() {
  const [form, setForm] = useState(EMPTY);
  const [companyId, setCompanyId] = useState(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/companies/mine");
      if (res.data) {
        const { name = "", description = "", sector = "", website = "", country = "", city = "", size = "" } = res.data;
        setForm({ name, description, sector, website, country, city, size });
        setCompanyId(res.data.company_id);
      }
    } catch {
      // aucun profil entreprise existant
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");
    setSaving(true);
    try {
      const res = await api.put("/companies/mine", form);
      setCompanyId(res.data.company_id);
      setMessage("Profil entreprise enregistré.");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const inputCls =
    "w-full border border-slate-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent";

  return (
    <div data-testid="employer-company" className="bg-white rounded-xl border border-slate-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-semibold text-brand-navy flex items-center gap-2">
          <Building2 className="w-5 h-5 text-brand-blue" />
          Mon entreprise (profil public)
        </h2>
        {companyId && (
          <Link
            data-testid="company-public-link"
            to={`/entreprises/${companyId}`}
            className="text-xs font-medium text-brand-turquoise hover:underline flex items-center gap-1"
          >
            Voir la page publique <ExternalLink className="w-3 h-3" />
          </Link>
        )}
      </div>
      {error && (
        <div data-testid="company-form-error" className="mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-2">{error}</div>
      )}
      {message && (
        <div data-testid="company-form-success" className="mb-3 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-4 py-2">{message}</div>
      )}
      <form onSubmit={save} className="grid md:grid-cols-3 gap-3">
        <input data-testid="company-name-input" required value={form.name} onChange={set("name")} placeholder="Nom de l'entreprise *" className={inputCls} />
        <input data-testid="company-sector-input" value={form.sector} onChange={set("sector")} placeholder="Secteur d'activité" className={inputCls} />
        <select data-testid="company-size-select" value={form.size} onChange={set("size")} className={`${inputCls} bg-white`}>
          <option value="">Taille</option>
          {SIZES.map((s) => (
            <option key={s} value={s}>{s} employés</option>
          ))}
        </select>
        <input data-testid="company-website-input" value={form.website} onChange={set("website")} placeholder="Site web (https://…)" className={inputCls} />
        <input data-testid="company-country-input" value={form.country} onChange={set("country")} placeholder="Pays" className={inputCls} />
        <input data-testid="company-city-input" value={form.city} onChange={set("city")} placeholder="Ville" className={inputCls} />
        <textarea data-testid="company-description-input" value={form.description} onChange={set("description")} placeholder="Description de l'entreprise" rows={2} className={`${inputCls} md:col-span-3`} />
        <button
          data-testid="company-save-button"
          type="submit"
          disabled={saving}
          className="md:col-span-3 bg-brand-blue text-white font-semibold rounded-lg px-4 py-2.5 hover:bg-brand-navy transition-colors duration-200 disabled:opacity-50"
        >
          {saving ? "Enregistrement…" : "Enregistrer le profil entreprise"}
        </button>
      </form>
    </div>
  );
}
